import { Card, CardContent } from "@/components/ui/card";
import { Quote, Star } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselApi,
} from "@/components/ui/carousel";
import { useEffect, useState } from "react";

const patientTestimonials = [
  {
    name: "Marina Santos",
    treatment: "Lentes de Contato Dental",
    text: "Sempre quis um sorriso mais harmônico, mas tinha medo de ficar artificial. A Dra. Natália entendeu perfeitamente o que eu buscava: algo natural que valorizasse meu rosto. O resultado ficou exatamente como imaginei. Me sinto mais confiante e o melhor: ninguém percebe que fiz. Parece meu sorriso, só que melhor.",
    rating: 5,
  },
  {
    name: "Rafael Oliveira",
    treatment: "Harmonização Facial",
    text: "O atendimento foi incrível desde o início. A Dra. Natália explicou cada etapa do tratamento de forma clara, tirou todas as minhas dúvidas. Fiz harmonização facial buscando um resultado discreto e ela conseguiu suavizar as linhas de expressão sem perder minha identidade. Fiquei muito satisfeito com o cuidado e profissionalismo.",
    rating: 5,
  },
  {
    name: "Juliana Costa",
    treatment: "Clareamento Dental",
    text: "Tinha receio de clarear os dentes por conta da sensibilidade, mas a equipe me passou total segurança. Fizeram um protocolo personalizado para mim e o processo foi muito tranquilo. O resultado ficou lindo, um branco natural, e não tive nenhum desconforto. Recomendo demais!",
    rating: 5,
  },
  {
    name: "Carlos Mendes",
    treatment: "Tratamento Periodontal",
    text: "Sofria com sangramento na gengiva há anos e estava perdendo dentes. A Dra. Natália foi muito acolhedora, explicou que o problema tinha solução e me mostrou todo o planejamento. Hoje minha gengiva está saudável, não sangra mais e consegui preservar meus dentes. Só tenho a agradecer pelo cuidado e dedicação.",
    rating: 5,
  },
  {
    name: "Amanda Silva",
    treatment: "Invisalign",
    text: "Sempre quis alinhar os dentes, mas não queria usar aparelho metálico. O Invisalign foi perfeito para mim! A Dra. Natália explicou todo o processo, mostrou a simulação digital de como ficaria meu sorriso. Hoje, um ano depois, estou com os dentes alinhados e foi muito mais confortável do que imaginei.",
    rating: 5,
  },
  {
    name: "Fernando Lima",
    treatment: "Restaurações Estéticas",
    text: "Tinha várias restaurações antigas escurecidas que me deixavam muito insegura ao sorrir. A Dra. Natália fez um trabalho impecável, as restaurações em resina ficaram imperceptíveis. Ela tem um cuidado incrível com os detalhes. Hoje sorrio sem pensar duas vezes!",
    rating: 5,
  },
  {
    name: "Patricia Alves",
    treatment: "Cirurgia Plástica Gengival",
    text: "Meu sorriso mostrava muita gengiva e isso sempre me incomodou. Fiz a cirurgia plástica gengival e o resultado foi exatamente o que eu esperava: um sorriso mais harmônico e proporcional. A recuperação foi muito tranquila e a equipe me acompanhou em todos os momentos. Me sinto outra pessoa!",
    rating: 5,
  },
  {
    name: "Bruno Costa",
    treatment: "Bioestimulador de Colágeno",
    text: "Queria um tratamento que melhorasse a qualidade da minha pele de forma natural e progressiva. O bioestimulador foi a escolha certa. A Dra. Natália explicou que os resultados viriam aos poucos e foi exatamente assim. Minha pele está mais firme, com viço, e ninguém nota que fiz nada - só dizem que estou bem!",
    rating: 5,
  },
];

const Testimonials = () => {
  const [testimonialsApi, setTestimonialsApi] = useState<CarouselApi | null>(null);
  const [current, setCurrent] = useState(0);
  const [isAutoPlayActive, setIsAutoPlayActive] = useState(true);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    if (!testimonialsApi) {
      return;
    }

    setCurrent(testimonialsApi.selectedScrollSnap());

    testimonialsApi.on("select", () => {
      setCurrent(testimonialsApi.selectedScrollSnap());
    });
  }, [testimonialsApi]);

  useEffect(() => {
    if (!testimonialsApi || !isAutoPlayActive || isHovering) {
      return;
    }

    const autoplay = setInterval(() => {
      testimonialsApi.scrollNext();
    }, 5000);

    return () => clearInterval(autoplay);
  }, [testimonialsApi, isAutoPlayActive, isHovering]);

  const handleUserInteraction = () => {
    setIsAutoPlayActive(false);
  };

  return (
    <section className="py-6 sm:py-8 lg:py-10 overflow-x-hidden" style={{ backgroundColor: '#F6F5F5' }}>
      <div className="container mx-auto px-4">
        {/* Depoimentos dos Pacientes */}
        <div>
          <div className="text-center mb-16 animate-fade-in-up">
            <p className="text-accent font-inter text-sm uppercase tracking-wider mb-3 inline-block px-4 py-2 rounded-full border border-accent/30 bg-accent/5">
              Depoimentos
            </p>
            <h2 className="font-kiona text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mt-4 mb-4 uppercase">
              Quem Confia Recomenda
            </h2>
            <p className="text-muted-foreground font-inter text-lg max-w-2xl mx-auto">
              Transformações reais, cuidado humano e resultados naturais. Veja o que nossos pacientes têm a dizer sobre sua experiência conosco.
            </p>
          </div>

          <Carousel
            setApi={setTestimonialsApi}
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full max-w-7xl mx-auto overflow-hidden"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <CarouselContent className="-ml-4">
              {patientTestimonials.map((testimonial, index) => (
                <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <Card className="bg-card backdrop-blur-sm border-border/50 h-full">
                    <CardContent className="p-6 flex flex-col h-full">
                      <Quote className="w-8 h-8 text-accent mb-4 opacity-50" />
                      <div className="flex gap-1 mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star
                            key={i}
                            className="w-4 h-4 fill-accent text-accent"
                          />
                        ))}
                      </div>
                      <p className="text-muted-foreground font-inter text-sm leading-relaxed mb-4 flex-grow">
                        "{testimonial.text}"
                      </p>
                      <div className="border-t border-border/30 pt-4">
                        <p className="text-foreground font-semibold font-inter text-base">
                          {testimonial.name}
                        </p>
                        <p className="text-muted-foreground font-inter text-xs mt-1">
                          {testimonial.treatment}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex items-center justify-center gap-4 mt-8">
              <CarouselPrevious 
                className="static translate-x-0 translate-y-0"
              />
              <div className="flex gap-2">
                {patientTestimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setIsAutoPlayActive(false);
                      testimonialsApi?.scrollTo(index);
                    }}
                    className={`w-2 h-2 rounded-full transition-all duration-300 p-2 ${
                      index === current 
                        ? 'bg-accent w-8' 
                        : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                    }`}
                    aria-label={`Ir para depoimento ${index + 1}`}
                  />
                ))}
              </div>
              <CarouselNext 
                className="static translate-x-0 translate-y-0"
              />
            </div>
          </Carousel>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
